import { defineStore } from 'pinia'
import type { Currency, Language, QueryKeys, Site } from '~/types/storeTypes'
import { CurrencyCode } from '~/types/storeTypes'
import languagesToLocalesDto from '~/utils/languagesToLocalesDto'
import type { BlogPostsPostInterface } from '~/types/blogPostTypes'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'
import type { ILocales } from '~/types/localeTypes'
import type { FrontendSiteMarketplaceConfig } from '~/types/sites/sitesFrontendTypes'

function getInitialState(): IStoreConfigState {
  return {
    productLayout: ProductLayout.Grid,
    isMobile: true,
    queryKeys: null,
    isTablet: false,
    site: undefined,
    areKeysDisplay: false,
    currencies: [],
    selectedCurrency: undefined,
    conversionCurrency: null,
    hasAnyBlogPosts: false,
    loaded: false,
    allQueryKeys: null,
    availableLocales: null,
    allCurrencies: [],
    marketplace_config: null,
  }
}

export const useStoreConfig = defineStore('storeConfig', {
  state: (): IStoreConfigState => getInitialState(),
  getters: {
    getCategoryDocKey: (state) => {
      return state.queryKeys?.categoryDocKey ?? ''
    },
    getCurrentKeys: (state) => {
      return state.queryKeys
    },
    getAreKeysDisplayed: (state) => {
      return state.areKeysDisplay
    },
    getSite: (state) => {
      return state.site
    },
    getAllCurrencies: (state) => {
      return state.allCurrencies
    },
    getCurrencies: (state) => {
      return state.currencies
    },
    getSelectedCurrency: (state) => {
      return state.selectedCurrency
    },
    getConversionCurrency: (state): Currency | null => {
      return state.conversionCurrency
    },
    getQueryKeys: (state) => {
      return state.allQueryKeys
    },
    getIsMobile: (state) => {
      return state.isMobile
    },
    getProductLayout: (state) => {
      return state.productLayout
    },
    getCurrentSiteLanguage: (state): Language | null => {
      const locale = useState<string>('locale.setting')
      const languageCode = locale.value.toUpperCase()

      return state.site?.languages?.find((language: Language) => language.iso_code === languageCode) ?? null
    },
    getHasAnyBlogPosts: (state) => {
      return state.hasAnyBlogPosts
    },
    getAvailableLocales: (state): ILocales | null => {
      return state.availableLocales ?? null
    },
    getIsActiveLayout: (state) => (layout: ProductLayout) => {
      return state.productLayout === layout
    },
    getIsLoaded: (state) => (): boolean => {
      return state.loaded
    },
  },
  actions: {
    toggleKeysDisplay() {
      this.areKeysDisplay = !this.areKeysDisplay
    },
    setLayout(layout: ProductLayout) {
      this.productLayout = layout
    },
    setIsMobile(value: boolean, isTablet?: boolean) {
      this.isMobile = value
      this.isTablet = isTablet ?? false
    },
    setSite(value: Partial<Site>) {
      this.site = value
      this.availableLocales = languagesToLocalesDto(value)
    },
    setAllCurrencies(currencies: Currency[]): void {
      this.allCurrencies = currencies
    },
    setCurrencies(currencies: Currency[]) {
      this.currencies = currencies
      this.selectedCurrency = currencies.find((currency) => currency.iso_code === CurrencyCode.Eur) ?? currencies[0]
    },
    setConversionCurrency(currency: Currency | null) {
      this.conversionCurrency = currency
    },
    setSelectedCurrency(value: Currency) {
      this.selectedCurrency = value
    },
    async getStoreConfig() {
      if (this.loaded && this.getSite && this.getQueryKeys) {
        return
      }

      const locale = useState<string>('locale.setting')

      const { blogs, conversionCurrency, currencies, filteredCurrencies, searchKeys, site } = await $fetch<{
        currencies: Currency[]
        filteredCurrencies: Currency[]
        conversionCurrency: Currency | null
        site: Pick<Site, 'languages' | 'name' | 'url'>
        blogs: BlogPostsPostInterface[]
        searchKeys: QueryKeys[]
      }>('/api/store-config', {
        method: 'POST',
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        ...fetchRetryParameters,
        body: {
          langIso: locale.value,
        },
      })

      this.setAllCurrencies(currencies)
      this.setCurrencies(filteredCurrencies)
      this.setConversionCurrency(conversionCurrency)
      this.setSite(site)
      this.setCurrentStoreQueryKeys(searchKeys)
      this.setHasAnyBlogPosts(blogs)
      this.setLoaded(true)
    },
    setCurrentStoreQueryKeys(data: QueryKeys[]) {
      this.allQueryKeys = data
    },
    setHasAnyBlogPosts(data: BlogPostsPostInterface[]) {
      this.hasAnyBlogPosts = data.length > 0
    },
    initCurrencyById(currencyId: number) {
      const currency = this.currencies?.find((currency: Currency) => currency.id === currencyId)
      if (currency) {
        this.setSelectedCurrency(currency)
      }
    },
    setCurrentKeys(queryKeys: QueryKeys | null): void {
      this.queryKeys = queryKeys
    },
    setLoaded(value: boolean): void {
      this.loaded = value
    },
  },
})

export enum ProductLayout {
  Grid = 'GRID',
  List = 'LIST',
}

export enum ContentType {
  Products = 'p',
  Offers = 'o',
}

export interface IStoreConfigState {
  productLayout: ProductLayout
  selectedCurrency?: Currency
  conversionCurrency: Currency | null
  isMobile: boolean
  isTablet: boolean
  queryKeys: QueryKeys | null
  allQueryKeys?: QueryKeys[] | null
  site?: Partial<Site>
  areKeysDisplay: boolean
  availableLocales?: ILocales | null
  allCurrencies?: Currency[]
  currencies?: Currency[]
  hasAnyBlogPosts: boolean
  loaded: boolean
  marketplace_config: FrontendSiteMarketplaceConfig | null
}
